import ArticleTemplate from '@/pages/article/components/ArticleTemplate';
import JsonLd from '@/components/base/JsonLd';
import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sugargoosheet.homes';

const warehouseSchema = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: 'Sugargoo Warehouse Guide: How Storage, QC Photos & Processing Work',
  description: 'Complete Sugargoo warehouse guide covering how items are received, stored, and photographed. Learn about storage duration limits and QC photo service.',
  url: `${siteUrl}/sugargoo-warehouse-guide`,
  datePublished: '2026-08-06',
  dateModified: '2026-08-06',
  author: { '@type': 'Organization', name: 'Sugargoo Spreadsheet' },
  publisher: { '@type': 'Organization', name: 'Sugargoo Spreadsheet', url: `${siteUrl}/` },
  image: 'https://readdy.ai/api/search-image?query=Modern%20organized%20warehouse%20storage%20concept%20with%20neatly%20stacked%20fashion%20boxes%20on%20shelves%2C%20warm%20cream%20and%20beige%20tones%2C%20soft%20overhead%20lighting%2C%20clean%20logistics%20aesthetic%2C%20minimalist%20industrial%20photography%20style&width=1200&height=600&seq=article-ware-hero&orientation=landscape',
};

export default function WarehouseGuidePage() {
  return (
    <>
      <JsonLd data={warehouseSchema} />
    <ArticleTemplate
      title="Sugargoo Warehouse Guide: How Storage, QC Photos & Processing Work"
      subtitle="Everything about the Sugargoo warehouse system — from item receipt and quality control photography to storage duration limits and the complete processing workflow."
      description="Complete Sugargoo warehouse guide covering storage, QC photos, and processing workflow."
      canonicalUrl="https://sugargoosheet.homes/sugargoo-warehouse-guide"
      breadcrumbLabel="Warehouse Guide"
      imageSrc="https://readdy.ai/api/search-image?query=Modern%20organized%20warehouse%20storage%20concept%20with%20neatly%20stacked%20fashion%20boxes%20on%20shelves%2C%20warm%20cream%20and%20beige%20tones%2C%20soft%20overhead%20lighting%2C%20clean%20logistics%20aesthetic%2C%20minimalist%20industrial%20photography%20style&width=1200&height=600&seq=article-ware-hero&orientation=landscape"
      imageAlt="Sugargoo warehouse storage and QC process guide"
      readTime="8 min read"
    >
      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          How the Sugargoo Warehouse System Works
        </h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          The <strong>Sugargoo</strong> warehouse is the backbone of the entire shopping experience. When you purchase items through the <strong>Sugargoo Spreadsheet</strong>, products do not ship directly to your international address. Instead, they are first delivered to a Sugargoo warehouse facility where they undergo inspection, photography, and storage before being consolidated into your outgoing shipment.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-4">
          This warehouse-first approach provides several critical benefits. Items are verified against their listing descriptions — staff check that the correct product, size, and color have been received. If discrepancies are found, you have the opportunity to request returns or exchanges before the item ever leaves China, saving you from the headache and expense of international returns.
        </p>
        <p className="text-foreground-700 leading-relaxed">
          For <strong>Sugargoo Spreadsheet</strong> users who purchase from multiple categories, the warehouse consolidation feature is invaluable. You can accumulate items over time — a pair of shoes one week, a hoodie the next — and ship everything together in one consolidated package. This dramatically reduces per-item shipping costs compared to sending each purchase individually.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Sugargoo QC Photo Service Explained
        </h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Quality Control photography is one of Sugargoo most appreciated features. For a small additional fee, warehouse staff will take detailed photographs of your purchased items from multiple angles. These photos show you exactly what you are getting — the actual product, not a stock image — before you commit to international shipping costs.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {[
            { icon: 'ri-camera-line', title: 'Multi-Angle Photos', desc: 'Staff photograph items from front, back, side, and detail angles so you can inspect every aspect of the product.' },
            { icon: 'ri-zoom-in-line', title: 'Detail Close-ups', desc: 'Close-up shots of logos, stitching, materials, and tags help verify authenticity and quality before shipping.' },
            { icon: 'ri-ruler-line', title: 'Size Verification', desc: 'Photos often include measurement references so you can confirm sizing matches the listing description.' },
          ].map((item) => (
            <div key={item.title} className="flex flex-col items-center text-center p-4 bg-background-100 rounded-lg border border-background-200/60">
              <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                <i className={item.icon}></i>
              </span>
              <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
              <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Storage Duration and Processing Timeline
        </h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Sugargoo typically offers free storage for a limited period after items arrive at the warehouse — usually around 90 days, though this can vary. This generous window gives <strong>Sugargoo Spreadsheet</strong> users plenty of time to accumulate items across multiple shopping sessions before consolidating and shipping everything together.
        </p>
        <p className="text-foreground-700 leading-relaxed">
          After the free storage period, items may incur a small daily fee. It is a good practice to plan your shopping accordingly — using the <Link to="/sugargoo-spreadsheet-shopping-guide" className="text-primary-500 hover:text-primary-600 font-medium">Sugargoo shopping guide</Link> to time your purchases so items arrive at the warehouse within a reasonable window for consolidation and shipping.
        </p>
      </section>
    </ArticleTemplate>
    </>
  );
}